import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useState, useEffect } from 'react'
import { type program } from 'core/types'
import { sendGet } from 'hooks/use-fetch'
import { Column, ColumnSort, ColumnType, type RowData } from 'Components/Table/DataTable/DataTable.model'
import DataTable from 'Components/Table/DataTable'
import { Autocomplete } from 'Components/AutoComplete'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { FormatDateValue } from 'core/Utilities'
import { ENROLLMENT, ENROLLMENT_MANAGEMENT_DETAILS } from 'Pages/Routes'
import { useNavigate, generatePath, useParams } from 'react-router'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { type SearchCriteria, usePagination, type PaginationProps } from 'hooks/use-pagination'
import { usePermissions } from 'hooks/use-permissions'

interface EnrollmentResponseModel {
  id: number
  child: ChildResponse
  programName: string
}

interface ChildResponse {
  id: number
  fullName: string
  dateOfBirth: string
  yearsMonthsAge: string
  birthGender: string
  ethCode: string
  isHomeless?: boolean
  povertyPercentage: number
  uic: string
}

interface EnrollmentData {
  id: number
  childId: number
  fullName: string
  dateOfBirth: string
  yearsMonthsAge: string
  yearsMonthsAgeOn91: string
  birthGender: string
  ethCode: string
  isHomeless: string
  poverty: number
  uic: string
  program: string
}

export interface SessionEnrollmentListProps {
  sessionId: number
}

const nameColumn = new Column('fullName', 'Student Name')

const dateOfBirthColumn = new Column('dateOfBirth', 'DOB', ColumnType.CUSTOM)
dateOfBirthColumn.customCellGenerator = row => {
  return <div className="d-flex f-align-items-center">
    <span>{FormatDateValue(row.dateOfBirth)}</span>
  </div>
}
const ageColumn = new Column('yearsMonthsAge', 'Age(y.m)')
const ageOnColumn = new Column('yearsMonthsAgeOn91', 'Age on 9/1(y.m)')
const genderColumn = new Column('birthGender', 'Gender')
const ethCodeColumn = new Column('ethCode', 'Eth.Code')
const homelessColumn = new Column('isHomeless', 'Homeless')
const PovertyColumn = new Column('poverty', 'Poverty %')
const uicColumn = new Column('uic', 'UIC')
const typeColumn = new Column('program', 'Type', ColumnType.CUSTOM)
typeColumn.customCellGenerator = row => {
  return <div>
    {row.program !== null && <Chip icon={<FiberManualRecordIcon className={'cyan-icon'} />} label={row.program} size='small' className={'cyan-indicator'} />}
  </div>
}

const columns = [nameColumn, dateOfBirthColumn, ageColumn, ageOnColumn, genderColumn, ethCodeColumn, typeColumn, PovertyColumn, homelessColumn, uicColumn]

export function SessionEnrollmentList (props: SessionEnrollmentListProps): JSX.Element {
  const [enrollments, setEnrollments] = useState<EnrollmentData[]>([])
  const [selectedProgram, setSelectedProgram] = useState<string>('')
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [programs, setPrograms] = useState<Array<{ id: string | number, name: string }>>([])
  const nav = useNavigate()
  const accountID = useAccountId()?.id ?? 0
  const params = useParams()
  const paginationProps: PaginationProps<RowData> = {
    rows: enrollments,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)
  const perms = usePermissions()

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    const route = generatePath(ENROLLMENT, { id: row.id })
    nav(route, { state: { redirectText: 'Session Details' } })
  }

  const addClick = (): void => {
    setShowConfirmation(true)
  }

  const handleConfrim = (): void => {
    const route = generatePath(ENROLLMENT_MANAGEMENT_DETAILS, { serviceId: params.serviceId ?? '' })
    nav(route, { state: { redirectText: 'Session Details' } })
  }

  const onProgramChange = (e: any, value: { id: string | number, name: string } | null): void => {
    if (value != null) {
      setSelectedProgram(value.name)
      const fieldArray: SearchCriteria[] = []
      fieldArray.push({
        field: 'program',
        value: value.name
      })
      pagination.setSearchFields(fieldArray)
    } else {
      setSelectedProgram('')
      pagination.setSearchFields([])
    }
  }

  useEffect(() => {
    async function fetchPrograms (): Promise<void> {
      const { response } = await sendGet(`/ServiceProviderDetails/GetProviderPrograms?id=${accountID}`, {})
      if (response !== null) {
        const listOfPrograms: Array<{ id: string | number, name: string }> = (response as program[]).map(p => { return { id: p.name, name: p.name } })
        setPrograms(listOfPrograms)
      }
      const { response: sessionEnrollmentList } = await sendGet(`/Enrollment/GetEnrollmentsBySessionId?sessionId=${props.sessionId}`, {})
      if (sessionEnrollmentList !== null) {
        const enrollmentData = sessionEnrollmentList.map((e: EnrollmentResponseModel) => {
          return {
            id: e.id,
            childId: e.child.id,
            fullName: e.child.fullName,
            dateOfBirth: e.child.dateOfBirth,
            yearsMonthsAge: e.child.yearsMonthsAge,
            yearsMonthsAgeOn91: e.child.yearsMonthsAge,
            birthGender: e.child.birthGender,
            ethCode: e.child.ethCode,
            isHomeless: e.child.isHomeless === true ? 'Yes' : 'No',
            poverty: e.child.povertyPercentage,
            uic: e.child.uic,
            program: e.programName
          }
        })
        setEnrollments(enrollmentData)
      }
    }
    void fetchPrograms()
  }, [])

  return (<>
    <Box sx={{ boxSizing: 'border-box', alignItems: 'center', p: '4px 0px', gap: '10px', display: 'flex', flexDirection: 'row' }}>
      <Autocomplete
        label='Program'
        name='status'
        onChange={onProgramChange}
        options={programs}
        width='200px'
        value={selectedProgram}
      />
      {perms.Enrollment_Edit &&
        <Box justifyContent='flex-end' display='flex' width='100%' >
          <Button color='secondary' variant='contained' data-testid="addbutton" onClick={addClick}>Add Student</Button>
        </Box>
      }
    </Box>
    <div>
      <Box sx={{ width: '100%', padding: '30px 20px', borderRadius: '16px', background: '#fff' }}>
        <DataTable
          name='enrollmentList'
          columns={columns}
          rows={pagination.internalRows}
          page={pagination.page}
          hasCheckbox={false}
          onPageChange={pagination.handleChangePage}
          onRowClick={rowClick}
          totalRecords={pagination.recordCount}
          onSortChange={(col, sorts) => {
            pagination.onSortChange(col, sorts[0].order)
          }}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          initialColumnSorts={[new ColumnSort('fullName')]}
          loading={false}
        />
      </Box>
    </div>

    {showConfirmation && <Dialog
      open={showConfirmation}
      data-testid="confirmationModal"
      onClose={() => { setShowConfirmation(false) }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {'Confirmation'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          This will take you directly to the enrollment screen and kick off the process of adding a new student there.  Do you want to continue?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setShowConfirmation(false) }}>Cancel</Button>
        <Button onClick={handleConfrim} autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>}

  </>)
}
